import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format, parseISO } from "date-fns";
import { fi } from "date-fns/locale";
import { DailyUsage } from "@/hooks/useApiUsage";

interface UsageChartProps {
  data: DailyUsage[];
  isLoading?: boolean;
}

const SERVICE_COLORS: Record<string, string> = {
  resend: "hsl(var(--primary))",
  "46elks": "hsl(var(--chart-2))",
};

export function UsageChart({ data, isLoading }: UsageChartProps) {
  // Transform data for chart (pivot by date)
  const chartData = useMemo(() => {
    const byDate: Record<string, Record<string, number>> = {};

    data.forEach((item) => {
      if (!byDate[item.date]) {
        byDate[item.date] = { date: item.date as unknown as number };
      }
      byDate[item.date][item.service] = item.count;
    });

    // Fill in missing dates
    const dates = Object.keys(byDate).sort();
    if (dates.length === 0) return [];

    const result: Array<{ date: string; resend: number; "46elks": number }> = [];
    const startDate = parseISO(dates[0]);
    const endDate = parseISO(dates[dates.length - 1]);

    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = format(d, "yyyy-MM-dd");
      result.push({
        date: dateStr,
        resend: byDate[dateStr]?.resend || 0,
        "46elks": byDate[dateStr]?.["46elks"] || 0,
      });
    }

    return result;
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Päivittäinen käyttö</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse bg-muted rounded w-full h-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Päivittäinen käyttö</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Ei käyttödataa saatavilla
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Päivittäinen käyttö (30 päivää)</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => format(parseISO(value), "d.M.", { locale: fi })}
              className="text-xs"
            />
            <YAxis className="text-xs" />
            <Tooltip
              labelFormatter={(value) =>
                format(parseISO(value as string), "d.M.yyyy", { locale: fi })
              }
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "var(--radius)",
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="resend"
              name="Sähköposti"
              stroke={SERVICE_COLORS.resend}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="46elks"
              name="SMS"
              stroke={SERVICE_COLORS["46elks"]}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
